"use client";

import React, { useState, useEffect, memo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  NotificationProvider,
  useNotificationContext,
} from "./hooks/NotificationProvider";
import { AuthProvider, useAuth } from "./hooks/useAuth.jsx";
import { USER_TYPES } from "./constants/userTypes";
import {
  UrgentCaseNotificationModal,
  TelemedicineSessionModal,
} from "./components/modals";
import AnnouncementModal from "./components/modals/AnnouncementModal";
import { PostModalProvider, usePostModal } from "./contexts/PostModalContext";
import PostDetailModal from "./components/modals/PostDetailModal";
import RouteManager from "./components/middleware/RouteManager";
import { PostUploadProvider } from "./contexts/PostUploadContext";
import UploadStatus from "./components/common/UploadStatus";
import { initializeLocalStorageMonitoring, getLocalStorageHealth } from "./utils/localStorageChecker";
import Navbar from "./components/account/Navbar";
import "./App.css";

// All components are now imported by RouteManager
import "./i18n";
import "./utils/sessionUtils";
import { initializeEcho } from "./services/echo";
// All components are now imported by RouteManager

import MainReceptionPage from "./pages/reception/MainReceptionPage.jsx";
import { ScrollToTop } from "./components/common";

// Simple placeholder components for pages under development

const EmergencyPage = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: '"Red Hat Display", sans-serif',
    }}
  >
    <div
      style={{
        textAlign: "center",
        padding: "40px",
        background: "#FFFFFF",
        borderRadius: "16px",
        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
      }}
    >
      <h1 style={{ color: "#1F2937", marginBottom: "16px" }}>
        Emergency Submission
      </h1>
      <p style={{ color: "#6B7280", marginBottom: "24px" }}>
        This page is coming soon!
      </p>
      <button
        onClick={() => window.history.back()}
        style={{
          padding: "12px 24px",
          background: "#EF4444",
          color: "#FFFFFF",
          border: "none",
          borderRadius: "12px",
          fontFamily: '"Red Hat Display", sans-serif',
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Go Back
      </button>
    </div>
  </div>
);

const CreatePostPage = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: '"Red Hat Display", sans-serif',
    }}
  >
    <div
      style={{
        textAlign: "center",
        padding: "40px",
        background: "#FFFFFF",
        borderRadius: "16px",
        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
      }}
    >
      <h1 style={{ color: "#1F2937", marginBottom: "16px" }}>Create Post</h1>
      <p style={{ color: "#6B7280", marginBottom: "24px" }}>
        Share your pet's joy with the community!
      </p>
      <button
        onClick={() => window.history.back()}
        style={{
          padding: "12px 24px",
          background: "#8548A8",
          color: "#FFFFFF",
          border: "none",
          borderRadius: "12px",
          fontFamily: '"Red Hat Display", sans-serif',
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Go Back
      </button>
    </div>
  </div>
);

// Component to render the urgent case modal inside Router context
const UrgentCaseModalWrapper = () => {
  const {
    urgentCaseModalOpen,
    urgentCaseData,
    closeUrgentCaseModal,
    isLoading,
    telemedicineModalOpen,
    telemedicineData,
    closeTelemedicineModal,
    handleAcceptAndTreat,
  } = useNotificationContext();

  return (
    <>
      <UrgentCaseNotificationModal
        isOpen={urgentCaseModalOpen}
        onClose={closeUrgentCaseModal}
        notificationData={urgentCaseData}
        isLoading={isLoading}
        onAcceptAndTreat={handleAcceptAndTreat}
      />
      <TelemedicineSessionModal
        isOpen={telemedicineModalOpen}
        onClose={closeTelemedicineModal}
        notificationData={telemedicineData}
        isLoading={isLoading}
      />
    </>
  );
};

// Component to handle global system announcements via WebSocket
const SystemAnnouncementWrapper = () => {
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [announcementData, setAnnouncementData] = useState(null);

  useEffect(() => {
    // Setup listener when Echo is available
    const setupListener = () => {
      if (window.Echo) {
        window.Echo.channel('system-announcements')
          .listen('.SystemAnnouncementEvent', (event) => {
            console.log("Received system announcement!", event);
            setAnnouncementData(event);
            setAnnouncementOpen(true);
            
            // Auto close if there's a duration set and it's not 0 or null
            if (event.duration > 0) {
              setTimeout(() => {
                setAnnouncementOpen(false);
              }, event.duration);
            }
          });
      }
    };

    // If Echo is already there, set it up
    if (window.Echo) {
      setupListener();
    } else {
      // Otherwise wait briefly for it to initialize
      const checkEchoInterval = setInterval(() => {
        if (window.Echo) {
          clearInterval(checkEchoInterval);
          setupListener();
        }
      }, 500);
      
      // Stop checking after 10 seconds to prevent memory leak
      setTimeout(() => clearInterval(checkEchoInterval), 10000);
    }

    return () => {
      if (window.Echo) {
        window.Echo.leaveChannel('system-announcements');
      }
    };
  }, []);

  return (
    <AnnouncementModal 
      isOpen={announcementOpen} 
      onClose={() => setAnnouncementOpen(false)} 
      announcement={announcementData} 
    />
  );
};

// Component to handle post modal events
const PostModalEventHandler = () => {
  const { openPostModal } = usePostModal();

  React.useEffect(() => {
    const handleOpenPostModal = (event) => {
      const { postId } = event.detail;
      if (postId) {
        openPostModal(postId);
      }
    };

    window.addEventListener('open-post-modal', handleOpenPostModal);
    return () => {
      window.removeEventListener('open-post-modal', handleOpenPostModal);
    };
  }, [openPostModal]);

  return null;
};

// Component to conditionally show Navbar based on route
// This component is OUTSIDE Routes, so Navbar persists across navigation
// Using a stable key ensures Navbar doesn't remount
const AppNavbar = memo(() => {
  const { getUserType } = useAuth();
  const userType = getUserType();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isHeaderParam = queryParams.get("isHeader");

  // Persist isHeader preference to sessionStorage so it survives navigation
  React.useEffect(() => {
    if (isHeaderParam !== null) {
      sessionStorage.setItem("webview_is_header", isHeaderParam);
    }
  }, [isHeaderParam]);

  const routesWithoutNavbar = ["/reception", "/admin", "/dev-admin", "/statistics"];

  // Determine effective isHeader value: Query Param > Session Storage > Default
  const storedIsHeader = sessionStorage.getItem("webview_is_header");
  const effectiveIsHeader = isHeaderParam !== null ? isHeaderParam : storedIsHeader;

  // Prioritize explicit override if present (in param or storage), otherwise fallback to route-based defaults
  const shouldHideNavbar = effectiveIsHeader !== null
    ? effectiveIsHeader === "false"
    : routesWithoutNavbar.some((route) => location.pathname.startsWith(route));

  // Check if current route is public (no navbar)
  const isPublicRoute = location.pathname === '/' ||
    location.pathname.startsWith('/auth/') ||
    location.pathname === '/about' ||
    location.pathname === '/terms' ||
    location.pathname === '/privacy' ||
    location.pathname === '/contact' ||
    location.pathname === '/sulhafa-care' ||
    location.pathname === '/sulhafa-connect' ||
    location.pathname === '/reset-password';

  // Don't show navbar for clinic moderators
  if (userType === USER_TYPES.CLINIC_MODERATOR) {
    return null;
  }

  // Don't show navbar on public routes or routes that should hide it
  if (isPublicRoute || shouldHideNavbar) {
    return null;
  }


  // Use a stable key to prevent remounting
  return <Navbar key="persistent-navbar" />;
}, () => {
  // Only re-render if we actually need to show/hide the navbar
  // This prevents unnecessary re-renders
  return false; // Always return false to prevent re-renders from props (we use location instead)
});

AppNavbar.displayName = 'AppNavbar';

// Layout component to handle global layout classes based on route/state
const MainLayout = ({ children }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isHeaderParam = queryParams.get("isHeader");

  // Persist isHeader preference to sessionStorage
  React.useEffect(() => {
    if (isHeaderParam !== null) {
      sessionStorage.setItem("webview_is_header", isHeaderParam);
    }
  }, [isHeaderParam]);

  const routesWithoutNavbar = ["/reception", "/admin", "/dev-admin", "/statistics"];

  // Determine effective isHeader value
  const storedIsHeader = sessionStorage.getItem("webview_is_header");
  const effectiveIsHeader = isHeaderParam !== null ? isHeaderParam : storedIsHeader;

  // Prioritize explicit override if present, otherwise fallback to route-based defaults
  const shouldHideNavbar = effectiveIsHeader !== null
    ? effectiveIsHeader === "false"
    : routesWithoutNavbar.some((route) => location.pathname.startsWith(route));

  return (
    <div className={`App ${shouldHideNavbar ? 'no-navbar' : ''}`}>
      {children}
    </div>
  );
};

const App = () => {
  // Get Google Client ID from environment variable
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Working version: Full app with working emergency modal (without NotificationProvider)
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyData, setEmergencyData] = useState(null);


  // Initialize localStorage monitoring on app load
  useEffect(() => {
    const initializeMonitoring = async () => {
      // Run localStorage check and monitoring setup
      await initializeLocalStorageMonitoring();

      // Store health status from the initialization result (no need to call getLocalStorageHealth again)

    };
    initializeMonitoring();
  }, []);

  // Clear localStorage only on actual browser close (not on tab switches or refreshes)
  useEffect(() => {
    // Set a session flag to track if this is a new session
    const sessionKey = 'app_session_active';
    const isNewSession = !sessionStorage.getItem(sessionKey);

    if (isNewSession) {
      // This is a new session - only clear if it's been more than 24 hours since last session
      const lastSessionTime = localStorage.getItem('last_session_time');
      const now = Date.now();
      const oneDayInMs = 24 * 60 * 60 * 1000;

      if (!lastSessionTime || (now - parseInt(lastSessionTime)) > oneDayInMs) {
        // Only clear localStorage if it's been more than 24 hours
        // CRITICAL: Skip clearing if we are on the token-login page to avoid race condition
        if (!window.location.pathname.includes('/auth/token-login')) {
          localStorage.clear();
        }

        // Update session time
        localStorage.setItem('last_session_time', now.toString());
        // Mark this session as active
        sessionStorage.setItem(sessionKey, 'true');
      }
    }

    // Handle browser close detection - only clear on actual browser close
    const handleBeforeUnload = () => {
      // Mark that we're about to unload
      sessionStorage.setItem('app_unloading', 'true');

      // Set a timeout to clear localStorage only if the page doesn't reload within 3 seconds
      setTimeout(() => {
        // If we're still unloading after 3 seconds, it's likely a browser close
        if (sessionStorage.getItem('app_unloading')) {
          localStorage.clear();

        }
      }, 3000);
    };

    // Handle page load - check if we're coming back from a refresh
    const handlePageLoad = () => {
      const wasUnloading = sessionStorage.getItem('app_unloading');
      if (wasUnloading) {
        // We were unloading but now we're loading again - this was a refresh
        sessionStorage.removeItem('app_unloading');

      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('load', handlePageLoad);

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('load', handlePageLoad);
    };
  }, []);

  // Periodic localStorage health check (every 5 minutes)
  useEffect(() => {
    const healthCheckInterval = setInterval(async () => {
      await getLocalStorageHealth();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(healthCheckInterval);
  }, []);

  // Initialize Echo WebSocket connection on app mount using @laravel/echo-react
  useEffect(() => {
    const configured = initializeEcho();
    if (!configured) {
      // Check if Reverb is configured
      const appKey = import.meta.env.VITE_REVERB_APP_KEY;
      if (!appKey) {
        // Reverb not configured - this is expected if using alternative notification methods

      } else {
        // Reverb is configured but Echo failed to initialize - this is a real issue
      }
    }
  }, []);

  // Handle Webview Overrides (language, theme)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // 1. Language override
    const langParam = params.get("language");
    if (langParam && (langParam === "ar" || langParam === "en")) {
      import("./i18n").then(({ default: i18n }) => {
        if (i18n.language !== langParam) {
          i18n.changeLanguage(langParam);
        }
      });
    }

    // 2. Theme override
    const themeParam = params.get("theme");
    if (themeParam === "dark") {
      document.body.classList.add("dark-theme");
    } else if (themeParam === "light") {
      document.body.classList.remove("dark-theme");
    }
  }, []);

  // Listen for emergency modal events
  React.useEffect(() => {
    const handleEmergencyEvent = (event) => {

      setEmergencyData(event.detail.emergencyData);
      setShowEmergencyModal(true);
    };

    window.addEventListener("show-emergency-modal", handleEmergencyEvent);
    return () =>
      window.removeEventListener("show-emergency-modal", handleEmergencyEvent);
  }, []);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthProvider>
        <Router>
          <NotificationProvider>
            <PostModalProvider>
              <MainLayout>
                <PostUploadProvider>
                  <ScrollToTop />
                  {/* Persistent Navbar - outside Routes so it doesn't remount */}
                  <AppNavbar />
                  {/* Render urgent case modal inside Router context */}
                  <UrgentCaseModalWrapper />
                  {/* Render system announcement modal globally */}
                  <SystemAnnouncementWrapper />
                  {/* Post Modal Event Handler */}
                  <PostModalEventHandler />
                  {/* Post Detail Modal */}
                  <PostDetailModal />
                  {/* Centralized Route Management */}
                  <RouteManager />

                  {/* Global Upload Progress Feedback */}
                  <UploadStatus />
                </PostUploadProvider>
                {/* Global Emergency Modal */}
                {showEmergencyModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "white",
                        padding: "30px",
                        borderRadius: "12px",
                        maxWidth: "600px",
                        width: "90%",
                        maxHeight: "80vh",
                        overflow: "auto",
                      }}
                    >
                      <h2 style={{ color: "#EF4444", marginBottom: "20px" }}>
                        🚨 Emergency Medical Case
                      </h2>
                      {/* Global Emergency Modal */}
                      {showEmergencyModal && (
                        <div
                          style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "rgba(0,0,0,0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000,
                          }}
                        >
                          <div
                            style={{
                              background: "white",
                              padding: "30px",
                              borderRadius: "12px",
                              maxWidth: "600px",
                              width: "90%",
                              maxHeight: "80vh",
                              overflow: "auto",
                            }}
                          >
                            <h2 style={{ color: "#EF4444", marginBottom: "20px" }}>
                              🚨 Emergency Medical Case
                            </h2>

                            {emergencyData && (
                              <div>
                                <div style={{ marginBottom: "15px" }}>
                                  <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                    Pet Guardian
                                  </h3>
                                  <p>
                                    <strong>Name:</strong>{" "}
                                    {emergencyData.owner?.name || "Unknown"}
                                  </p>
                                  <p>
                                    <strong>Contact:</strong>{" "}
                                    {emergencyData.owner?.contact || "Not available"}
                                  </p>
                                  <p>
                                    <strong>Email:</strong>{" "}
                                    {emergencyData.owner?.email || "Not available"}
                                  </p>
                                </div>
                                {emergencyData && (
                                  <div>
                                    <div style={{ marginBottom: "15px" }}>
                                      <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                        Pet Guardian
                                      </h3>
                                      <p>
                                        <strong>Name:</strong>{" "}
                                        {emergencyData.owner?.name || "Unknown"}
                                      </p>
                                      <p>
                                        <strong>Contact:</strong>{" "}
                                        {emergencyData.owner?.contact || "Not available"}
                                      </p>
                                      <p>
                                        <strong>Email:</strong>{" "}
                                        {emergencyData.owner?.email || "Not available"}
                                      </p>
                                    </div>

                                    <div style={{ marginBottom: "15px" }}>
                                      <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                        Pet Information
                                      </h3>
                                      <p>
                                        <strong>Name:</strong>{" "}
                                        {emergencyData.pet?.name || "Unknown"}
                                      </p>
                                      <p>
                                        <strong>Species:</strong>{" "}
                                        {emergencyData.pet?.species || "Unknown"}
                                      </p>
                                      <p>
                                        <strong>Breed:</strong>{" "}
                                        {emergencyData.pet?.breed || "Unknown"}
                                      </p>
                                      <p>
                                        <strong>Age:</strong>{" "}
                                        {emergencyData.pet?.age || "Unknown"}
                                      </p>
                                    </div>
                                    <div style={{ marginBottom: "15px" }}>
                                      <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                        Pet Information
                                      </h3>
                                      <p>
                                        <strong>Name:</strong>{" "}
                                        {emergencyData.pet?.name || "Unknown"}
                                      </p>
                                      <p>
                                        <strong>Species:</strong>{" "}
                                        {emergencyData.pet?.species || "Unknown"}
                                      </p>
                                      <p>
                                        <strong>Breed:</strong>{" "}
                                        {emergencyData.pet?.breed || "Unknown"}
                                      </p>
                                      <p>
                                        <strong>Age:</strong>{" "}
                                        {emergencyData.pet?.age || "Unknown"}
                                      </p>
                                    </div>

                                    <div style={{ marginBottom: "15px" }}>
                                      <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                        Emergency Details
                                      </h3>
                                      <p>
                                        <strong>Type:</strong>{" "}
                                        {emergencyData.emergency?.type || "Urgent"}
                                      </p>
                                      <p>
                                        <strong>Description:</strong>{" "}
                                        {emergencyData.emergency?.description ||
                                          "No description available"}
                                      </p>
                                      <p>
                                        <strong>Severity:</strong>{" "}
                                        {emergencyData.emergency?.severity || "High"}
                                      </p>
                                      {emergencyData.emergency?.symptoms &&
                                        emergencyData.emergency.symptoms.length > 0 && (
                                          <p>
                                            <strong>Symptoms:</strong>{" "}
                                            {emergencyData.emergency.symptoms.join(", ")}
                                          </p>
                                        )}
                                    </div>
                                    <div style={{ marginBottom: "15px" }}>
                                      <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                        Emergency Details
                                      </h3>
                                      <p>
                                        <strong>Type:</strong>{" "}
                                        {emergencyData.emergency?.type || "Urgent"}
                                      </p>
                                      <p>
                                        <strong>Description:</strong>{" "}
                                        {emergencyData.emergency?.description ||
                                          "No description available"}
                                      </p>
                                      <p>
                                        <strong>Severity:</strong>{" "}
                                        {emergencyData.emergency?.severity || "High"}
                                      </p>
                                      {emergencyData.emergency?.symptoms &&
                                        emergencyData.emergency.symptoms.length > 0 && (
                                          <p>
                                            <strong>Symptoms:</strong>{" "}
                                            {emergencyData.emergency.symptoms.join(", ")}
                                          </p>
                                        )}
                                    </div>

                                    <div style={{ marginBottom: "20px" }}>
                                      <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                        Location
                                      </h3>
                                      <p>
                                        <strong>Address:</strong>{" "}
                                        {emergencyData.location?.address || "Not available"}
                                      </p>
                                      <p>
                                        <strong>Distance:</strong>{" "}
                                        {emergencyData.location?.distance ||
                                          "Not available"}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                <div style={{ marginBottom: "20px" }}>
                                  <h3 style={{ color: "#1F2937", marginBottom: "5px" }}>
                                    Location
                                  </h3>
                                  <p>
                                    <strong>Address:</strong>{" "}
                                    {emergencyData.location?.address || "Not available"}
                                  </p>
                                  <p>
                                    <strong>Distance:</strong>{" "}
                                    {emergencyData.location?.distance ||
                                      "Not available"}
                                  </p>
                                </div>
                              </div>
                            )}

                            <div
                              style={{
                                display: "flex",
                                gap: "10px",
                                justifyContent: "flex-end",
                              }}
                            >
                              <button
                                onClick={() => {

                                  setShowEmergencyModal(false);
                                  setEmergencyData(null);
                                  alert(
                                    "Emergency case accepted! Redirecting to case management..."
                                  );
                                }}
                                style={{
                                  padding: "12px 24px",
                                  background: "#10B981",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                }}
                              >
                                Accept Case
                              </button>
                              <button
                                onClick={() => {

                                  setShowEmergencyModal(false);
                                  setEmergencyData(null);
                                  alert(
                                    "Emergency case rejected. Other veterinarians will be notified."
                                  );
                                }}
                                style={{
                                  padding: "12px 24px",
                                  background: "#EF4444",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                }}
                              >
                                Reject Case
                              </button>
                              <button
                                onClick={() => {
                                  setShowEmergencyModal(false);
                                  setEmergencyData(null);
                                }}
                                style={{
                                  padding: "12px 24px",
                                  background: "#6B7280",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                }}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          onClick={() => {

                            setShowEmergencyModal(false);
                            setEmergencyData(null);
                            alert(
                              "Emergency case accepted! Redirecting to case management..."
                            );
                          }}
                          style={{
                            padding: "12px 24px",
                            background: "#10B981",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontWeight: "600",
                          }}
                        >
                          Accept Case
                        </button>
                        <button
                          onClick={() => {

                            setShowEmergencyModal(false);
                            setEmergencyData(null);
                            alert(
                              "Emergency case rejected. Other veterinarians will be notified."
                            );
                          }}
                          style={{
                            padding: "12px 24px",
                            background: "#EF4444",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontWeight: "600",
                          }}
                        >
                          Reject Case
                        </button>
                        <button
                          onClick={() => {
                            setShowEmergencyModal(false);
                            setEmergencyData(null);
                          }}
                          style={{
                            padding: "12px 24px",
                            background: "#6B7280",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontWeight: "600",
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </MainLayout>
            </PostModalProvider>
          </NotificationProvider>
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
};

export default App;

